import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import { toast } from "react-toastify";
import { useApproveSignupRequest } from "../../../hooks/react-query/signup-requests/useApproveSignupRequest";
import { useRejectSignupRequest } from "../../../hooks/react-query/signup-requests/useRejectSignupRequest";
import { useSignupRequests } from "../../../hooks/react-query/signup-requests/useSignupRequests";
import { ProfilePicture } from "../../UserProfile/common/ProfilePicture";
import { SignupRequest } from "./signupRequestsMockData";
// Reuses AdminRequests.tsx's classes verbatim (.admin_tile, .admin_request_item, etc.) - this tab
// is intentionally styled identically to its sibling "Requests" tab, so there's no separate scss
// file to maintain here.
import "../../../assets/styles/pages/adminRequests.scss";

export const UserRequests = () => {
  const { requests } = useSignupRequests();
  const approveMutation = useApproveSignupRequest();
  const rejectMutation = useRejectSignupRequest();

  // Plain single-click approve, mirroring AdminRequests.tsx's own handleApprove. The backend
  // creates the user itself from the signup request's own stored fields (services/user.py's
  // approve_signup_request) and auto-assigns the "BASIC" view group; the admin sets any other
  // groups/views afterward via the normal Users table, same as any other user.
  const handleApprove = (request: SignupRequest) => {
    approveMutation.mutate(
      { id: request.id, domain: window.location.origin },
      {
        onSuccess: (result) => {
          if (result.status === "success") {
            toast.success(`${request.first_name} ${request.last_name} approved`);
          } else {
            toast.error(`Couldn't approve ${request.first_name} ${request.last_name} - please try again.`);
          }
        },
      }
    );
  };

  const handleReject = (request: SignupRequest) => {
    rejectMutation.mutate(
      { id: request.id },
      {
        onSuccess: (result) => {
          if (result.status === "success") {
            toast.success(`Rejected ${request.first_name} ${request.last_name}`);
          } else {
            toast.error(`Couldn't reject ${request.first_name} ${request.last_name} - please try again.`);
          }
        },
      }
    );
  };

  return (
    <div className="admin_tile admin_requests">
      <div className="admin_requests_header">
        <div className="admin_requests_header_left">
          <PersonAddIcon />
          <h3>USER REQUESTS</h3>
        </div>
        <span className="admin_requests_pending">{requests.length} pending</span>
      </div>
      <div className="admin_requests_divider" />

      <div className="admin_requests_list">
        {requests.length === 0 ? (
          <p className="admin_requests_empty">No pending requests.</p>
        ) : (
          requests.map((request) => (
            <div className="admin_request_item" key={request.id} data-test-id={`user-request-${request.id}`}>
              <ProfilePicture firstname={request.first_name || request.email || " "} lastname={request.last_name || " "} />
              <div className="admin_request_item_details">
                <p className="admin_request_item_name">
                  {request.first_name} {request.last_name}
                </p>
                <p className="admin_request_item_email">{request.email}</p>
                <p className="admin_request_item_meta">
                  {request.business_function}
                  <span className="admin_request_item_dot">•</span>
                  {request.department}
                  <span className="admin_request_item_dot">•</span>
                  {request.geo_scope}
                  <span className="admin_request_item_dot">•</span>
                  {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}
                </p>
              </div>
              <div className="admin_request_item_actions">
                <button
                  className="admin_request_item_accept"
                  onClick={() => handleApprove(request)}
                  data-test-id={`user-request-approve-${request.id}`}
                >
                  <CheckIcon fontSize="small" />
                  Approve
                </button>
                <button
                  className="admin_request_item_reject"
                  onClick={() => handleReject(request)}
                  data-test-id={`user-request-reject-${request.id}`}
                >
                  <CloseIcon fontSize="small" />
                  Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
